import { Map, Record, Set } from 'immutable'

const VIEW_IS_LOADING = 'VIEW_IS_LOADING'
const VIEW_HAS_LOADED = 'VIEW_HAS_LOADED'
const VIEW_FAILED_TO_LOAD = 'VIEW_FAILED_TO_LOAD'

export const hasGraphQLError = error =>
  error.graphQLErrors ? error.graphQLErrors.length > 0 : true

// ACTIONS
export function viewIsLoading(viewId, ...queryNames) {
  return {
    type: VIEW_IS_LOADING,
    payload: {
      viewId,
      queryNames,
    },
  }
}

export function viewHasLoaded(viewId, ...queryNames) {
  return {
    type: VIEW_HAS_LOADED,
    payload: {
      viewId,
      queryNames,
    },
  }
}

export function viewFailedToLoad(viewId, queryName, error) {
  if (!hasGraphQLError(error)) {
    return
  }
  return {
    type: VIEW_FAILED_TO_LOAD,
    payload: {
      viewId,
      queryName,
      error,
    },
  }
}

export function injectViewStateAsMeta(originalAction, viewStateAction) {
  return {
    ...originalAction,
    meta: originalAction.meta
      ? {
          ...originalAction.meta,
          viewState: viewStateAction,
        }
      : {
          viewState: viewStateAction,
        },
  }
}

// SELECTORS
export function getRawViewState(state, viewId) {
  return state.get(viewId) || ViewState()
}

export function getViewState(state, viewId) {
  const { pristine, loading, hasLoadedOnce, errors } = getRawViewState(
    state,
    viewId
  )
  return {
    pristine,
    loading: loading.size > 0,
    errors: errors.size > 0 ? errors.toArray() : false,
    hasLoadedOnce,
  }
}

// STATE & REDUCER
const ViewState = new Record({
  pristine: true,
  hasLoadedOnce: false,
  loading: Set(),
  errors: Map(),
})

const initialState = Map()

export default function reducer(state = initialState, action) {
  if (action.meta && action.meta.viewState) {
    state = reducer(state, action.meta.viewState)
  }
  const { type, payload } = action

  switch (type) {
    case VIEW_IS_LOADING: {
      const { viewId, queryNames } = payload
      const viewState = getRawViewState(state, viewId)
      const { loading, errors } = viewState
      const newLoading = queryNames.reduce(
        (loading, queryName) => loading.add(queryName),
        loading
      )
      const newErrors = queryNames.reduce(
        (errors, queryName) => errors.remove(queryName),
        errors
      )

      return state.set(
        viewId,
        viewState
          .set('loading', newLoading)
          .set('errors', newErrors)
          .set('pristine', false)
      )
    }
    case VIEW_HAS_LOADED: {
      const { viewId, queryNames } = payload
      const viewState = getRawViewState(state, viewId)
      const { loading } = viewState
      const newLoading = queryNames.reduce(
        (loading, queryName) => loading.remove(queryName),
        loading
      )
      const hasLoadedOnce =
        newLoading.size === 0 ? true : viewState.get('hasLoadedOnce')
      const newViewState = viewState
        .set('loading', newLoading)
        .set('pristine', false)
        .set('hasLoadedOnce', hasLoadedOnce)
      return state.set(viewId, newViewState)
    }
    case VIEW_FAILED_TO_LOAD: {
      const { viewId, queryName, error } = payload
      const viewState = getRawViewState(state, viewId)
      const newViewState = viewState
        .set('errors', viewState.errors.set(queryName, error))
        .set('loading', viewState.loading.remove(queryName))
        .set('hasLoadedOnce', false)
        .set('pristine', false)

      return state.set(viewId, newViewState)
    }
    default:
      return state
  }
}
